import React, { forwardRef, useEffect, useCallback } from 'react'
import { cva } from 'class-variance-authority'
import { VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

const modalOverlayVariants = cva(
  [
    'cg-fixed cg-inset-0 cg-z-50 cg-flex cg-items-center cg-justify-center',
    'cg-bg-black/40',
    'cg-animate-fade-in',
  ].join(' ')
)

const modalContentVariants = cva(
  [
    'cg-relative cg-bg-white cg-shadow-drop cg-font-sans',
    'cg-animate-slide-up',
    'cg-max-h-[90vh] cg-overflow-y-auto',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'cg-w-[400px] cg-max-w-[90vw]',
        md: 'cg-w-[522px] cg-max-w-[90vw]',
        lg: 'cg-w-[640px] cg-max-w-[90vw]',
        xl: 'cg-w-[800px] cg-max-w-[90vw]',
      },
      rounded: {
        lg: 'cg-rounded-lg',
        xl: 'cg-rounded-xl',
        '2xl': 'cg-rounded-2xl',
        '3xl': 'cg-rounded-3xl',
      },
    },
    defaultVariants: {
      size: 'md',
      rounded: '3xl',
    },
  }
)

export interface ModalProps extends VariantProps<typeof modalContentVariants> {
  /** Whether the modal is visible */
  open: boolean
  /** Called when the modal requests to close (overlay click or Escape) */
  onClose?: () => void
  /** Whether clicking the overlay closes the modal */
  closeOnOverlayClick?: boolean
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean
  /** Additional class for the content card */
  className?: string
  /** Additional class for the overlay */
  overlayClassName?: string
  children: React.ReactNode
}

const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      closeOnOverlayClick = true,
      closeOnEscape = true,
      size,
      rounded,
      className,
      overlayClassName,
      children,
    },
    ref
  ) => {
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape' && closeOnEscape && onClose) {
          onClose()
        }
      },
      [closeOnEscape, onClose]
    )

    useEffect(() => {
      if (open) {
        document.addEventListener('keydown', handleKeyDown)
        document.body.style.overflow = 'hidden'
      }
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.body.style.overflow = ''
      }
    }, [open, handleKeyDown])

    if (!open) return null

    return (
      <div
        className={twMerge(clsx(modalOverlayVariants(), overlayClassName))}
        onClick={closeOnOverlayClick && onClose ? onClose : undefined}
        aria-modal="true"
        role="dialog"
      >
        <div
          ref={ref}
          className={twMerge(clsx(modalContentVariants({ size, rounded }), className))}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    )
  }
)

Modal.displayName = 'Modal'

export { Modal, modalContentVariants, modalOverlayVariants }
